class TestimonialsMarquee extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.viewport = this.querySelector('[data-testimonials-viewport]');
    this.track = this.querySelector('[data-testimonials-track]');
    this.group = this.querySelector('[data-testimonials-group]');
    if (!this.viewport || !this.track || !this.group || this.group.children.length < 2) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.speed = Math.min(30, Math.max(10, Number(this.dataset.speed) || 20));
    this.editorSelected = false;
    this.addEventListener('focusin', event => {
      if (event.target.matches(':focus-visible')) this.read(event.target);
    }, { signal });
    this.addEventListener('focusout', () => queueMicrotask(() => {
      if (!this.contains(document.activeElement) && !this.editorSelected) {
        this.removeAttribute('data-reading');
        this.viewport.scrollLeft = 0;
      }
    }), { signal });
    this.addEventListener('shopify:block:select', event => {
      const slide = event.target.closest('[data-testimonial]');
      if (!slide || !this.group.contains(slide)) return;
      this.editorSelected = true;
      this.read(slide);
    }, { signal });
    this.addEventListener('shopify:block:deselect', () => {
      this.editorSelected = false;
      if (!this.contains(document.activeElement)) this.removeAttribute('data-reading');
    }, { signal });
    this.motion.addEventListener('change', () => this.update(), { signal });
    this.resizeObserver = new ResizeObserver(() => this.measure());
    this.resizeObserver.observe(this.viewport);
    this.resizeObserver.observe(this.group);
    this.update();
  }

  update() {
    const animate = this.dataset.autoplay === 'true' && !this.motion.matches;
    if (animate && !this.clone) {
      this.clone = this.group.cloneNode(true);
      this.clone.removeAttribute('data-testimonials-group');
      this.clone.setAttribute('data-testimonials-clone', '');
      this.clone.setAttribute('aria-hidden', 'true');
      this.clone.setAttribute('inert', '');
      this.clone.querySelectorAll('[id], [data-shopify-editor-block]').forEach(node => {
        node.removeAttribute('id');
        node.removeAttribute('data-shopify-editor-block');
      });
      this.clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach(node => node.setAttribute('tabindex', '-1'));
      this.track.append(this.clone);
    } else if (!animate) {
      this.clone?.remove();
      this.clone = null;
    }
    this.toggleAttribute('data-enhanced', animate);
    this.measure();
  }

  measure() {
    if (!this.clone) return;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const distance = this.group.getBoundingClientRect().width + gap;
    this.track.style.setProperty('--testimonial-distance', `${distance}px`);
    this.track.style.setProperty('--testimonial-duration', `${distance / this.speed}s`);
  }

  read(target) {
    this.setAttribute('data-reading', '');
    cancelAnimationFrame(this.focusFrame);
    this.focusFrame = requestAnimationFrame(() => {
      if (this.isConnected && target !== this.viewport) target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.focusFrame);
    this.clone?.remove();
    this.clone = null;
    this.removeAttribute('data-enhanced');
    this.removeAttribute('data-reading');
    this.track?.style.removeProperty('--testimonial-distance');
    this.track?.style.removeProperty('--testimonial-duration');
  }
}

if (!customElements.get('testimonials-marquee')) customElements.define('testimonials-marquee', TestimonialsMarquee);
